import { useEffect } from 'react';

import { getPlatformAppsTemplates } from '@services';
import {
  resetPlatformAppsInstances,
  resetPlatformAppsTemplates,
  setPlatformAppsFetcher,
  setPlatformAppsLoader,
  setPlatformAppsTemplates,
} from '@slices';
import { getPlatformAppsInstances } from '@thunks/platformApps.thunks';
import { contextNamesSelector } from '@selectors';
import { useDispatch, useSelector } from '@hooks';
import { invariant, toastifyResponseError } from '@utils';

export const usePlatformApps = () => {
  const dispatch = useDispatch();
  const { clusterName, organizationName, projectName } =
    useSelector(contextNamesSelector);

  useEffect(() => {
    const getTemplates = async () => {
      try {
        invariant(organizationName);
        invariant(clusterName);
        invariant(projectName);

        dispatch(setPlatformAppsLoader({ templates: true }));

        const { items: templates } = await getPlatformAppsTemplates();

        dispatch(setPlatformAppsTemplates(templates));
        dispatch(setPlatformAppsFetcher({ templates: true }));
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        dispatch(setPlatformAppsLoader({ templates: false }));
      }
    };

    if (organizationName && clusterName && projectName) {
      getTemplates();
    } else {
      dispatch(resetPlatformAppsTemplates());
    }
  }, [organizationName, clusterName, projectName, dispatch]);

  useEffect(() => {
    const getInstances = async () => {
      try {
        invariant(organizationName);
        invariant(clusterName);
        invariant(projectName);

        dispatch(setPlatformAppsLoader({ instances: true }));

        dispatch(
          getPlatformAppsInstances({
            organizationName,
            clusterName,
            projectName,
          }),
        );

        dispatch(setPlatformAppsFetcher({ instances: true }));
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        dispatch(setPlatformAppsLoader({ instances: false }));
      }
    };

    let intervalId: NodeJS.Timer | null = null;

    if (organizationName && clusterName && projectName) {
      getInstances();

      intervalId = setInterval(() => {
        getInstances();
      }, 15000);
    } else {
      dispatch(resetPlatformAppsInstances());
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [organizationName, clusterName, projectName, dispatch]);
};
